import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
import lodash from 'lodash';
import { connect } from 'react-redux';
import { Close } from '@material-ui/icons';
import Tooltip from '@material-ui/core/Tooltip';
import { withStyles } from '@material-ui/core/styles';
import styles from './UserBulkActionList.scss';
import icon from 'styles/icons/binder-icon.scss';
import DefaultMaterialTable from 'common/util/materialTable';
import { Button, BUTTON } from 'components/common/Button/Button';
import { options, theme, columns } from './UserBulkActionListConfig';
import {
    loadAllUserBinders,
    resetAllUserBinders,
} from 'part11/actions/users';
import { addUserToBinderAsAdmin, setShowModal } from 'part11/actions/userBulkAdd';
import AddUserToBinderModal from './AddUserToBinderModal/AddUserToBinderModal';
import AddUserToBinderContainer from './AddUserToBinderModal/AddUserToBinderContainer';
import ConfirmChangeDialog from 'complionAdmin/components/GroupPermissionsSelector/ConfirmChangeDialog';

export const UserBulkActionList = props => {
    const [saved, setSaved] = useState(false);

    useEffect(() => {
        const { institutionId, userId, loadAllUserBinders } = props;
        loadAllUserBinders(institutionId, userId);

        return function cleanup() {
            props.resetAllUserBinders();
        };
    }, []);

    function openModal() {
        setSaved(false);
        props.setShowModal(true);
    }

    function handleOnSave() {
        if (saved) {
            const { user, selectedBinderIds, institutionId, addUserToBinderAsAdmin } = props;
            addUserToBinderAsAdmin(institutionId, user.id, selectedBinderIds);
            setSaved(false);
        } else {
            setSaved(true);
        }
    }

    function handleCloseModal() {
        setSaved(false);
        props.setShowModal(false);
    }

    function handleRemove(rowData) {
        const binderId = lodash.get(rowData, 'data.id', undefined);
        const studyId = lodash.get(rowData, 'data.studyId', undefined);

        const { user, institutionId, removeUserFromBinder, removeUserFromStudyBinder } = props;

        // A binder tied to a study is a 4.0 binder; otherwise it's a 3.0 binder.
        if (studyId) {
            removeUserFromStudyBinder(binderId, user, institutionId);
        } else {
            removeUserFromBinder(binderId, user, institutionId);
        }
    }

    const BinderToolTip = withStyles({
        tooltip: {
            display: 'inline-block',
            opacity: '0.85',
            color: 'white',
            backgroundColor: '#28282E',
            padding: '5px',
            fontSize: 14,
        }
    })(Tooltip);

    const TableRow = ({ rowData, handleRemove }) => {
        const pi = lodash.get(rowData, 'data.principalInvestigator', undefined);
        const name = lodash.get(rowData, 'data.name', undefined);
        const site = lodash.get(rowData, 'data.site', undefined);

        return (
            <tr className={styles.MuiTableRowCustom} data-cy="UserBulkActionList-return">
                <td className={styles.MuiTableCellCustom} data-cy="UserBulkActionList-names">{name}</td>
                <td className={styles.MuiTableCellCustom} data-cy="UserBulkActionList-pis">{pi}</td>
                <td className={styles.MuiTableCellCustom} data-cy="UserBulkActionList-sites">{site}</td>
                <td className={styles.MuiTableCellCustom}>
                    <BinderToolTip title='Remove User from Binder' enterDelay={250} placement='left-start' >
                        <span className={styles.close} data-cy="UserBulkActionList-span">
                            <Close onClick={() => handleRemove(rowData)} />
                        </span>
                    </BinderToolTip>
                </td>
            </tr>
        );
    };

    const { user, selectedBinderIds, allBinders } = props;
    const numBindersToAdd = selectedBinderIds.length;
    const tableData = allBinders;

    return (
        <div className={styles.container} data-cy="UserBulkActionList-returns">
            <header className={styles.header}>
                <span className={icon.binderIcon}></span>
                <span className={styles.title} data-cy="UserBulkActionList-tableName">Binders</span>
                <div className={styles.buttonContainer}>
                    <Button
                        bgcolor={BUTTON.BGCOLOR.BLUE}
                        color={BUTTON.COLOR.WHITE}
                        onClick={openModal}
                        data-cy="UserBulkActionList-openModal"
                    >
                        Add User as Admin
                    </Button>
                </div>
            </header>
            <section className={styles.listWrapper} data-cy="UserBulkActionList-section">
                <DefaultMaterialTable
                    options={options(tableData.length)}
                    theme={theme}
                    columns={columns}
                    data={tableData}
                    components={{
                        Row: props => {
                            return (
                                <TableRow
                                    rowData={props}
                                    handleRemove={handleRemove}
                                />
                            );
                        },
                    }}
                />
            </section>
            <AddUserToBinderModal
                isOpen={props.showModal}
                onClose={handleCloseModal}
                isDisabled={!props.selectedBinderIds.length || props.isAddUserToBinderLoading}
                onSave={handleOnSave}
                title={`Add ${user.displayName} to Binders as Admin`}
                onCancelText={'Cancel'}
                onSaveText={'Save'}
                bodyContainer={<AddUserToBinderContainer user={user} />}
            />
            <ConfirmChangeDialog
                open={saved}
                onCancel={() => setSaved(false)}
                onConfirm={handleOnSave}
                title={'Add User as Admin Confirmation'}
                content={`You are about to add ${user.displayName} as an admin to ${numBindersToAdd} binder(s). Do you want to proceed?`}
                confirmText="Yes"
                cancelText="No"
            />
        </div>
    );
};

UserBulkActionList.propTypes = {
    userId: PropTypes.string.isRequired,
};

export default connect((state) => {
    const allBinderData = lodash.get(state, 'allUserBinders', {});
    const isLoading = lodash.get(allBinderData, 'isLoading', false);
    const allBinders = lodash.get(allBinderData, 'existingData', []);
    const selectedBinderIds = lodash.get(state, 'userBinders.selectedBinderIds', []);
    const isAddUserToBinderLoading = lodash.get(state, 'addUserToBinders.isLoading', false);
    const showModal = lodash.get(state, 'userBinders.showModal', false);

    return {
        allBinders,
        isLoading,
        selectedBinderIds,
        isAddUserToBinderLoading,
        showModal,
    };
}, {
    loadAllUserBinders,
    resetAllUserBinders,
    addUserToBinderAsAdmin,
    setShowModal
})(UserBulkActionList);
